import React, { useState, useRef } from "react";
import { Button, Card, Input, Alert, Tabs, Typography, Flex, Space } from "antd";
import { MobileOutlined, MailOutlined, LockOutlined, ArrowLeftOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TopNavigation from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";
import { AuthenticationService } from "../services/AuthenticationService";
import { User } from "../models/User";

const { Title, Text, Link } = Typography;

type Method = "phone" | "email";
type Step   = "contact" | "otp" | "newPassword";

const ForgotPasswordPage: React.FC = () => {
    const navigate = useNavigate();

    const [method,      setMethod]      = useState<Method>("phone");
    const [step,        setStep]        = useState<Step>("contact");
    const [contact,     setContact]     = useState("");
    const [otp,         setOtp]         = useState(["", "", "", "", "", ""]);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPass, setConfirmPass] = useState("");
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState("");
    const [currentUser, setCurrentUser] = useState<User>(new User());

    const otpRefs = [
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
    ];

    const switchMethod = (m: Method) => {
        setMethod(m);
        setContact("");
        setStep("contact");
        setOtp(["", "", "", "", "", ""]);
        setError("");
    };

    const handleOtpChange = (val: string, index: number) => {
        const digit = val.replace(/[^0-9]/g, "").slice(-1);
        const next  = [...otp];
        next[index] = digit;
        setOtp(next);
        if (digit && index < 5) otpRefs[index + 1].current?.focus();
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs[index - 1].current?.focus();
    };

    const handleContactSubmit = async () => {
        const cleaned = contact.trim();
        if (!cleaned) { setError(method === "phone" ? "Please enter your phone number." : "Please enter your email address."); return; }
        setError("");
        setLoading(true);
        try {
            if (method === "email") {
                setCurrentUser(await AuthenticationService.sendPasswordResetCodeByEmail(cleaned));
            } else {
                const user = await AuthenticationService.getUserByPhone(cleaned);
                setCurrentUser(user);
                await AuthenticationService.sendPasswordResetCodeBySms(cleaned);
            }
            setStep("otp");
        } catch {
            setError(method === "phone"
                ? "This phone number is not linked to any account."
                : "Could not send reset link. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async () => {
        if (otp.join("").length < 6) { setError("Please enter all 6 digits."); return; }
        setError("");
        setLoading(true);
        try {
            await AuthenticationService.verifyOtp(otp, currentUser);
            setStep("newPassword");
        } catch {
            setError("OTP verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!newPassword || !confirmPass) { setError("Please fill in both fields.");              return; }
        if (newPassword.length < 8)       { setError("Password must be at least 8 characters."); return; }
        if (newPassword !== confirmPass)   { setError("Passwords do not match.");                 return; }
        setError("");
        setLoading(true);
        try {
            await AuthenticationService.updatePassword(currentUser, newPassword);
            navigate("/logIn");
        } catch {
            setError("Failed to reset password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setLoading(true);
        try {
            if (method === "phone") await AuthenticationService.sendPasswordResetCodeBySms(contact);
            else                    await AuthenticationService.sendPasswordResetCodeByEmail(contact);
            setOtp(["", "", "", "", "", ""]);
        } catch {
            setError("Failed to resend code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const meta = {
        contact:     { title: "Reset your password",  subtitle: method === "phone" ? "Enter your registered phone to get a verification code." : "Enter your registered email to get a reset link." },
        otp:         { title: "Enter the code",        subtitle: `We sent a 6-digit code to ${contact}` },
        newPassword: { title: "Create new password",   subtitle: "Choose a strong password for your account." },
    };

    return (
        <>
            <TopNavigation />

            <Flex align="center" justify="center" style={{ minHeight: "100vh", background: "#f5f5f0", padding: "100px 24px 60px" }}>
                <div style={{ width: "100%", maxWidth: 440 }}>
                    <Card>

                        {/* Header */}
                        <Flex vertical align="center" gap={4} style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ margin: 0 }}>{meta[step].title}</Title>
                            <Text type="secondary" style={{ textAlign: "center", lineHeight: 1.6 }}>{meta[step].subtitle}</Text>
                        </Flex>

                        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

                        {/* ── Step 1: Contact ── */}
                        {step === "contact" && (
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <Tabs
                                    activeKey={method}
                                    onChange={(key) => switchMethod(key as Method)}
                                    items={[
                                        { key: "phone", label: <><MobileOutlined /> Phone</> },
                                        { key: "email", label: <><MailOutlined /> Email</> },
                                    ]}
                                />
                                <Input
                                    prefix={method === "phone" ? <MobileOutlined /> : <MailOutlined />}
                                    placeholder={method === "phone" ? "e.g. 082 123 4567" : "e.g. john@example.com"}
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                    disabled={loading}
                                    size="large"
                                    type={method === "email" ? "email" : "tel"}
                                />
                                <Button type="primary" size="large" block loading={loading} onClick={handleContactSubmit}>
                                    {method === "phone" ? "Send Code" : "Send Reset Link"}
                                </Button>
                            </Space>
                        )}

                        {/* ── Step 2: OTP ── */}
                        {step === "otp" && (
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <Flex gap={8}>
                                    {otp.map((digit, i) => (
                                        <input
                                            key={i}
                                            ref={otpRefs[i]}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(e.target.value, i)}
                                            onKeyDown={(e) => handleOtpKeyDown(e, i)}
                                            maxLength={1}
                                            style={{
                                                flex: 1, minWidth: 0, height: 58, textAlign: "center",
                                                fontSize: 22, fontWeight: 700, borderRadius: 12, outline: "none",
                                                border: `1.5px solid ${digit ? "#00AF66" : "#EDF1F7"}`,
                                                background: digit ? "#E6F7EF" : "#F7F9FC",
                                                color: digit ? "#00AF66" : "#1A1A2E",
                                            }}
                                        />
                                    ))}
                                </Flex>

                                <Text type="secondary" style={{ textAlign: "center", display: "block" }}>
                                    Didn't receive it?{" "}
                                    <Link onClick={handleResend}>Resend Code</Link>
                                </Text>

                                <Button type="primary" size="large" block loading={loading} disabled={loading || otp.join("").length < 6} onClick={handleOtpSubmit}>
                                    Verify Code
                                </Button>

                                <Link onClick={() => { setStep("contact"); setError(""); }} style={{ display: "block", textAlign: "center" }}>
                                    ← Change {method === "phone" ? "phone number" : "email"}
                                </Link>
                            </Space>
                        )}

                        {/* ── Step 3: New Password ── */}
                        {step === "newPassword" && (
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="New password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={loading}
                                    size="large"
                                    iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                />
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirm new password"
                                    value={confirmPass}
                                    onChange={(e) => setConfirmPass(e.target.value)}
                                    disabled={loading}
                                    size="large"
                                    iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>Minimum 8 characters</Text>
                                <Button type="primary" size="large" block loading={loading} onClick={handlePasswordReset}>
                                    Reset Password
                                </Button>
                            </Space>
                        )}

                    </Card>

                    {/* Back to login */}
                    <Flex justify="center" style={{ marginTop: 20 }}>
                        <Link onClick={() => navigate("/logIn")}>
                            <ArrowLeftOutlined /> Back to Login
                        </Link>
                    </Flex>
                </div>
            </Flex>

            <Footer />
        </>
    );
};

export default ForgotPasswordPage;